import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, PlayCircle, Clock, MapPin, Plus, BellRing, Repeat } from "lucide-react";
import TaskDialog from "@/components/tasks/TaskDialog";
import { loadStoredUser } from "@/lib/api";
import { Link } from "react-router-dom";

const STATUS_STYLES = {
  scheduled: "bg-secondary/15 text-secondary border-secondary/30",
  in_progress: "bg-warning/15 text-warning border-warning/30",
  completed: "bg-primary/15 text-primary border-primary/30",
  cancelled: "bg-muted text-muted-foreground",
  over_budget: "bg-danger/15 text-danger border-danger/40",
};

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const opts = { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  return d.toLocaleString(undefined, opts);
}

function bucketOf(t, now) {
  const start = new Date(t.scheduled_start);
  const isToday = start.toDateString() === now.toDateString();
  if (["completed", "cancelled"].includes(t.status)) return "past";
  if (isToday) return "today";
  if (start < now) return "past";
  return "upcoming";
}

export default function TasksPage() {
  const user = loadStoredUser();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [config, setConfig] = useState({ allow_worker_created: false });
  const [worksites, setWorksites] = useState([]);
  const [pushEnabled, setPushEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, w, prefs] = await Promise.all([
        api.get("/tenant/tasks", { params: { limit: 100 } }),
        api.get("/tenant/tasks-config").catch(() => ({ data: {} })),
        api.get("/time/my-worksites").catch(() => ({ data: { items: [] } })),
        api.get("/preferences").catch(() => ({ data: {} })),
      ]);
      setItems(r.data.items || []);
      setConfig(c.data || {});
      setWorksites(w.data.items || []);
      setPushEnabled(Boolean(prefs.data?.reminders?.web_push));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const buckets = useMemo(() => {
    const b = { today: [], upcoming: [], past: [] };
    for (const t of items) b[bucketOf(t, now)].push(t);
    for (const k of Object.keys(b)) b[k].sort((a, b2) => a.scheduled_start.localeCompare(b2.scheduled_start));
    return b;
  }, [items, now]);

  const startTask = async (t) => {
    try {
      const q = new Date(t.scheduled_start) - Date.now() > 60 * 60 * 1000 ? "?force=true" : "";
      await api.post(`/tenant/tasks/${t.id}/start${q}`);
      toast.success(`Started · ${t.title}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const completeTask = async (t) => {
    try {
      await api.post(`/tenant/tasks/${t.id}/complete`);
      toast.success(`Completed · ${t.title}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const switchTo = async (from, to) => {
    try {
      await api.post(`/tenant/tasks/${from.id}/switch`, { next_task_id: to.id });
      toast.success(`Switched to ${to.title}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inProgress = items.find((t) => t.status === "in_progress" || t.status === "over_budget");

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Your work</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Scheduled tasks</h1>
          <p className="mt-2 text-muted-foreground">
            Start tasks to auto clock-in with the task's context pre-filled. The corner HUD
            keeps track of running time, and we'll ping once if you go over budget.
          </p>
        </div>
        {config.allow_worker_created && (
          <Button onClick={() => setDialog(true)} data-testid="tasks-create-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Create task
          </Button>
        )}
      </div>

      {!pushEnabled && (
        <div className="flex items-center justify-between rounded-xl border border-warning/40 bg-warning/5 px-4 py-3 text-sm">
          <div className="flex items-center gap-2 text-warning">
            <BellRing className="h-4 w-4" />
            Push notifications are off — turn them on so you don't miss deadlines.
          </div>
          <Button asChild size="sm" variant="outline" data-testid="tasks-enable-push-link">
            <Link to="/app/settings/notifications">Enable</Link>
          </Button>
        </div>
      )}

      <Section title="Today" items={buckets.today} empty="Nothing on today." loading={loading}>
        {(t) => (
          <TaskCard
            key={t.id}
            task={t}
            worksites={worksites}
            inProgress={inProgress}
            onStart={() => startTask(t)}
            onComplete={() => completeTask(t)}
            onSwitch={() => switchTo(inProgress, t)}
          />
        )}
      </Section>

      <Section title="Upcoming (7 days)" items={buckets.upcoming} empty="No upcoming tasks." loading={loading}>
        {(t) => (
          <TaskCard
            key={t.id}
            task={t}
            worksites={worksites}
            inProgress={inProgress}
            onStart={() => startTask(t)}
            onComplete={() => completeTask(t)}
            onSwitch={() => switchTo(inProgress, t)}
          />
        )}
      </Section>

      <Section title="Recently completed" items={buckets.past.slice(0, 20)} empty="No history yet." loading={loading}>
        {(t) => <TaskCard key={t.id} task={t} worksites={worksites} inProgress={null} muted />}
      </Section>

      <TaskDialog
        open={dialog}
        onOpenChange={setDialog}
        workerId={user?.id}
        workerOptions={user ? [user] : []}
        worksiteOptions={worksites}
        onSaved={load}
      />
    </div>
  );
}

function Section({ title, items, empty, loading, children }) {
  return (
    <div className="space-y-2">
      <h2 className="text-lg font-semibold text-primary">{title}</h2>
      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
          {empty}
        </div>
      )}
      <div className="grid gap-2">{items.map(children)}</div>
    </div>
  );
}

function TaskCard({ task, worksites, inProgress, onStart, onComplete, onSwitch, muted }) {
  const wsName = (worksites.find((w) => w.id === task.worksite_id) || {}).name;
  const isRunning = task.status === "in_progress" || task.status === "over_budget";
  return (
    <Card
      className={`transition ${muted ? "opacity-70" : ""}`}
      data-testid={`task-card-${task.id}`}
    >
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${STATUS_STYLES[task.status]}`}>
              {task.status.replace("_", " ")}
            </span>
            {task.recurrence_rrule && (
              <Badge variant="outline" className="rounded-full text-[10px]">
                <Repeat className="mr-1 h-3 w-3" /> Repeating
              </Badge>
            )}
          </div>
          <div className="mt-1 truncate font-medium text-primary" data-testid={`task-title-${task.id}`}>
            {task.title}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmt(task.scheduled_start)} · {task.budget_minutes}m budget</span>
            {wsName && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {wsName}</span>}
          </div>
        </div>
        {!muted && (
          <div className="flex flex-shrink-0 gap-2">
            {isRunning ? (
              <Button size="sm" onClick={onComplete} data-testid={`task-complete-${task.id}`}>
                <CheckCircle2 className="mr-1.5 h-4 w-4" /> Complete
              </Button>
            ) : inProgress && inProgress.id !== task.id ? (
              <Button size="sm" variant="outline" onClick={onSwitch} data-testid={`task-switch-${task.id}`}>
                Switch here
              </Button>
            ) : (
              <Button size="sm" onClick={onStart} data-testid={`task-start-${task.id}`}>
                <PlayCircle className="mr-1.5 h-4 w-4" /> Start
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
